import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import EditSelectedAd from '../../components/EditSelectedAd';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SponsoredAdvert } from 'models/SponsoredAdvert';
import AdvertRow from '../../components/AdvertRow';
import { Params } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import useSearchParams from 'hooks/useSearchParams';
import ResolvePagination from 'components/ResolvePagination';
import PluscircleIcon from 'icons/Pluscircle.icon';
import { FIRST_PAGE, SMOOTH_SCROLL_CONFIGURATION } from 'data/global';
import useListingCardPlaceholder from 'hooks/useListingCardPlaceholder';
import useRefetch from 'hooks/useRefetch';
import useMatchMedia from 'hooks/useMatchMedia';

import './SponsoredAdverts.styles.scss';

type SponsoredAdvertsProps = {
  className?: string;
};

const LIMIT_SPONSORED_ADS_REQ = '3';

const SponsoredAdverts: React.FC<SponsoredAdvertsProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState(null);
  const [sponsoredAdverts, setSponsoredAdverts] = useState<
    Array<SponsoredAdvert>
  >([]);
  const { popServerError } = useContext(PopNotificationsContext);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const { params, setQueryParam } = useQueryParams<Params>();

  const editContainerRef = useRef<HTMLDivElement>(null);

  const isPhablet = useMatchMedia('isPhablet');

  const { refetchFlag, refetch } = useRefetch();

  const classes = classNames(
    'sponsored-adverts',

    className,
  );
  const editContainerClasses = classNames('sponsored-adverts__edit-container');

  const searchParams = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.sponsoredListings.getSponsoredAdverts({
          $limit: LIMIT_SPONSORED_ADS_REQ,
          ...searchParams,
        });

        setTotalPages(totalPages);
        setSponsoredAdverts(items);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [popServerError, searchParams, refetchFlag]);

  const loadingState = useListingCardPlaceholder(3);

  const updateSposoredAdverts = useCallback(
    (
      operation: 'delete' | 'add' | 'toggle-pause' | 'update',
      advert: SponsoredAdvert,
    ) => {
      switch (operation) {
        case 'add':
          setSponsoredAdverts((old) => [advert, ...old]);
          setSelectedItem(advert);
          return;
        case 'delete':
          setSponsoredAdverts((old) =>
            old.filter((el) => el._id !== advert._id),
          );
          setSelectedItem(null);
          refetch();
          return;
        case 'toggle-pause':
          return setSponsoredAdverts((old) =>
            old.map((el) =>
              el._id === advert._id
                ? {
                    ...el,
                    status: advert.status,
                  }
                : el,
            ),
          );
        case 'update':
          return setSponsoredAdverts((old) =>
            old.map((el) => (el._id === advert._id ? advert : el)),
          );
      }
    },
    [refetch],
  );

  useEffect(() => {
    if (sponsoredAdverts.length === 0 && +params.page !== 1) {
      setQueryParam('page', FIRST_PAGE);
    }
  }, [params, setQueryParam, sponsoredAdverts]);

  useEffect(() => {
    if (selectedItem)
      editContainerRef.current?.scrollIntoView({
        ...SMOOTH_SCROLL_CONFIGURATION,
        block: 'start',
      });
  }, [selectedItem]);

  return (
    <div className={classes}>
      <div className="sponsored-adverts__list">
        <div className="text-container">
          <p>{t('sponsoredListings')}</p>
          <p>{t('tapOneToEdit')}</p>
        </div>
        <ul>
          {isLoading
            ? loadingState
            : sponsoredAdverts.map((ad) => (
                <li key={ad._id}>
                  <AdvertRow
                    className={selectedItem?._id === ad._id ? 'selected' : ''}
                    onSelect={() => setSelectedItem(ad)}
                    ad={ad}
                  />
                </li>
              ))}
        </ul>
        <ResolvePagination totalPages={totalPages} />
      </div>

      <div className={editContainerClasses} ref={editContainerRef}>
        <div className="sponsored-adverts__edit-container__heading">
          <div>
            <p>
              {t(selectedItem ? 'editAD' : 'creatingNewAd', {
                adName: selectedItem?.title,
              })}
            </p>

            {selectedItem && (
              <Button
                onClick={() => setSelectedItem(null)}
                className="as-link linkalike-action-button"
                icon={PluscircleIcon}
              >
                {t(isPhablet ? 'newAd' : 'createNewAd')}
              </Button>
            )}
          </div>
          <p>{t('adContainerRandomMessage')}</p>
        </div>
        <EditSelectedAd
          selectedAd={selectedItem}
          addSponsoredAdvert={updateSposoredAdverts}
          updateSponsoredAdverts={updateSposoredAdverts}
        />
      </div>
    </div>
  );
};

export default SponsoredAdverts;
